import { Component } from "react"
import PropTypes from "prop-types"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import classNames from "classnames"
import * as actions from "./actions"
import reducers from "./reducers"
import displayFlash from "partners/scripts/display_flash"

import AppletMetadata from "./applet_metadata"
import FilterCodeComposer from "./filter_code_composer"
import TandaComposer from "./tanda_composer"
import TandaFields from "./tanda_fields"
import formatIngredientsMetadata from "./lib/format_ingredients_metadata"
import isAppletComplete from "./lib/is_applet_complete"
import SectionWithIcon from "./section_with_icon"

import keyBy from "lodash/keyBy"
import flatMap from "lodash/flatMap"
import mapValues from "lodash/mapValues"

import ActionIcon from "shared/components/icons/action_icon"
import DelayIcon from "shared/components/icons/delay_icon"
import FilterIcon from "shared/components/icons/filter_icon"
import MessageWithIcon from "shared/components/message_with_icon"
import PrivateIcon from "shared/components/icons/private_icon"
import PublicIcon from "shared/components/icons/public_icon"
import TriggerIcon from "shared/components/icons/trigger_icon"
import Spinner from "shared/components/spinner"
import InlineErrors from "shared/components/inline_errors"

import colors from "foundation/_colors_export.scss?export"
import Delay from "./delay"

class AppletComposerUI extends Component {
  componentDidMount() {
    window.App.Utils?.logCustomDatadogAction?.("applet_publish_show")
  }

  componentDidUpdate() {
    const { applet, errors, reduxActions, services } = this.props
    const currentTrigger = this.currentTrigger()

    applet.applet_actions.forEach((appletAction, index) => {
      const selectedActionData = this.selectedActionData(appletAction)
      const appletErrors = errors.forApplet.select(`/applet/applet_actions`).all
      const appletIncompatibilityErrors = Object.keys(appletErrors).some(key => {
        appletErrors[key].some(v => v.includes("compatible"))
      })

      // check if selected trigger service is compatible with selected action service
      if (
        selectedActionData.service &&
        selectedActionData.service.module_name &&
        applet.applet_trigger.channel_module_name &&
        selectedActionData.service.incompatible_channels.includes(applet.applet_trigger.channel_module_name)
      ) {
        reduxActions.incompatibleTrigger(
          selectedActionData.service.name,
          services.byModuleName[applet.applet_trigger.channel_module_name].name
        )
        reduxActions.removeAction(index)
      } else if (
        !selectedActionData.service &&
        Object.keys(appletErrors).length &&
        appletIncompatibilityErrors &&
        applet.applet_trigger.channel_module_name
      ) {
        reduxActions.clearIncompatibilityError()
      }

      // check if selected action is compatible with selected trigger
      if (
        currentTrigger &&
        selectedActionData.action &&
        selectedActionData.action.incompatible_triggers.includes(currentTrigger.full_module_name)
      ) {
        reduxActions.incompatibleTrigger(selectedActionData.action.name, currentTrigger.name)
        reduxActions.removeAction(index)
      } else if (selectedActionData.action && Object.keys(appletErrors).length && appletIncompatibilityErrors) {
        reduxActions.clearIncompatibilityError()
      }
    })
  }

  ingredientsText = () => {
    const allIngredientNodes = Array.from(
      document.querySelectorAll(".ingredients-text-field .growing-text-area textarea")
    )

    return (allIngredientNodes.length && allIngredientNodes.map(el => el.value).join(" ")) || ""
  }

  hasUselessQueries = textToMatch => {
    return !this.currentQueries()
      .map(step => {
        return `${step.channel.normalized_module_name}.${step.normalized_module_name}`
      })
      .some(normalizedStep => textToMatch.includes(normalizedStep))
  }

  createApplet = () => {
    const { applet, reduxActions, isSuggested, createOrUpdateAppletUrl, appletsUrl, appletShowUrl } = this.props

    const ingredientError = document.querySelector(".ingredients-text-field .ingredient-pill-error")
    if (ingredientError) {
      displayFlash("Please correct the invalid ingredients", "error")
      ingredientError.scrollIntoView({ block: "center" })
      return
    }

    const confirmation = []

    if (
      applet.applet_queries &&
      applet.applet_queries.length &&
      this.hasUselessQueries(this.ingredientsText().concat(" ", applet.filter_code || ""))
    ) {
      confirmation.push(
        window.confirm(
          "You’ve added a query but haven’t used it in your filter code or as an ingredient, are you sure you want to continue?"
        )
      )
    }

    if (applet.published) {
      confirmation.push(
        window.confirm(
          "Your Applet is published. Any alterations will be applied to all users of this Applet. Are you sure?"
        )
      )
    }

    confirmation.every(i => i) &&
      reduxActions.createApplet(
        applet,
        applet.published ? appletShowUrl : appletsUrl,
        isSuggested,
        createOrUpdateAppletUrl
      )
  }

  currentTrigger() {
    const { applet, triggers } = this.props
    const { channel_module_name, module_name } = applet.applet_trigger

    if (channel_module_name && module_name) {
      return triggers.byService[channel_module_name][module_name]
    }
  }

  currentQueries() {
    const { applet, queries } = this.props
    const currentQueries = []
    applet.applet_queries.forEach(aq => {
      const { channel_module_name, module_name } = aq
      if (channel_module_name && module_name) {
        currentQueries.push(queries.byService[channel_module_name][module_name])
      }
    })
    return currentQueries
  }

  selectedQueryData(appletQuery) {
    const { queries, services } = this.props
    const { channel_module_name, module_name } = appletQuery
    const selectedService = channel_module_name && services.byModuleName[channel_module_name]

    const selectedServiceQueries = selectedService && queries.byService[channel_module_name]

    const selectedQuery = module_name && selectedServiceQueries && selectedServiceQueries[module_name]

    return {
      service: selectedService,
      serviceQueries: selectedServiceQueries,
      query: selectedQuery,
    }
  }

  queryComposers() {
    const { applet, contextChannelId, reduxActions, services } = this.props

    // Nested set of actions that are already in use, so we can exclude them
    // from dropdowns.
    const currentQueries = {}
    applet.applet_queries.forEach(appletQuery => {
      const { channel_module_name, module_name } = appletQuery

      if (channel_module_name && module_name) {
        if (!currentQueries[channel_module_name]) {
          currentQueries[channel_module_name] = {}
        }
        currentQueries[channel_module_name][module_name] = true
      }
    })

    return applet.applet_queries.map((appletQuery, index) => {
      const selectedQueryData = this.selectedQueryData(appletQuery)
      const { channel_module_name } = appletQuery

      const selectedServiceQueriesArray =
        selectedQueryData.serviceQueries &&
        Object.values(selectedQueryData.serviceQueries).filter(
          query =>
            query === selectedQueryData.query ||
            !currentQueries[channel_module_name] ||
            !currentQueries[channel_module_name][query.module_name]
        )

      const icon = selectedQueryData.service ? (
        <img
          className="applet-composer-sidebar-service-logo"
          src={selectedQueryData.service.lrg_monochrome_image_url}
          title={selectedQueryData.service.name}
          alt={selectedQueryData.service.name}
        />
      ) : (
        <ActionIcon title="Query" />
      )

      const iconBackgroundColor = selectedQueryData.service && `#${selectedQueryData.service.brand_color}`

      let filteredQueryServices = services.queryServices

      const currentTrigger = this.currentTrigger()

      return (
        <SectionWithIcon
          key={index}
          icon={icon}
          sectionId={`query-${index}`}
          iconBackgroundColor={iconBackgroundColor}
          label="When"
        >
          <div className="fieldset composer-fieldset">
            <TandaComposer
              contextChannelId={contextChannelId}
              services={filteredQueryServices}
              onServiceSelect={service => {
                reduxActions.selectQueryService(index, service)
              }}
              selectedService={selectedQueryData.service}
              selectedServiceTandas={selectedServiceQueriesArray}
              onTandaSelect={query => {
                reduxActions.selectQuery(index, query)
              }}
              selectedTanda={selectedQueryData.query}
              onRemoveTanda={() => reduxActions.removeQuery(index)}
              tandaType="query"
              note="Queries help control when you run actions."
              enabled={!applet.published}
            >
              {this.queryFieldsComponent(appletQuery, index, currentTrigger)}
            </TandaComposer>
          </div>
        </SectionWithIcon>
      )
    })
  }

  queryFieldsComponent = (appletQuery, index, currentTrigger) => {
    const { queries, applet, errors, reduxActions, services, isMaker } = this.props
    const { channel_module_name, module_name, fields } = appletQuery

    if (!fields.length) {
      return null
    }

    const selectedService = channel_module_name && services.byModuleName[channel_module_name]

    const selectedQuery = selectedService && (queries.byService[channel_module_name] || {})[module_name]

    const fieldsByName = selectedQuery && keyBy(selectedQuery.fields, qf => qf.name)

    const ingredientsMetadata = formatIngredientsMetadata(currentTrigger, [], services.byModuleName)

    return (
      <TandaFields
        tandaType="query"
        appletFields={fields}
        fieldsByName={fieldsByName}
        onChange={(fieldIndex, field, data) => reduxActions.updateQueryField(index, fieldIndex, field, data)}
        onInvalidResolution={reduxActions.invalidFieldResolution}
        ingredientsMetadata={ingredientsMetadata}
        errors={errors.forApplet.select(`/applet/applet_queries/${index}/fields`)}
        invalidRemoteFields={errors.invalidRemoteFields}
        canEditHiddenField={!applet.published}
        canEditHiddenValueField={true}
        minimalView={isMaker && !applet.id}
      />
    )
  }

  selectedActionData(appletAction) {
    const { actions, services } = this.props
    const { channel_module_name, module_name } = appletAction
    const selectedService = channel_module_name && services.byModuleName[channel_module_name]

    const selectedServiceActions = selectedService && actions.byService[channel_module_name]

    const selectedAction = module_name && selectedServiceActions && selectedServiceActions[module_name]

    return {
      service: selectedService,
      serviceActions: selectedServiceActions,
      action: selectedAction,
    }
  }

  actionComposers(isAppletPublished) {
    const { actions, applet, contextChannelId, reduxActions, services } = this.props

    // Nested set of actions that are already in use, so we can exclude them
    // from dropdowns.
    const currentActions = {}
    applet.applet_actions.forEach(appletAction => {
      const { channel_module_name, module_name } = appletAction

      if (channel_module_name && module_name) {
        if (!currentActions[channel_module_name]) {
          currentActions[channel_module_name] = {}
        }
        currentActions[channel_module_name][module_name] = true
      }
    })

    return applet.applet_actions.map((appletAction, index) => {
      const selectedActionData = this.selectedActionData(appletAction)
      const { channel_module_name } = appletAction

      const selectedServiceActionsArray =
        selectedActionData.serviceActions &&
        Object.values(selectedActionData.serviceActions).filter(
          action =>
            action === selectedActionData.action ||
            !currentActions[channel_module_name] ||
            !currentActions[channel_module_name][action.module_name]
        )

      let fieldDefaults = selectedActionData.service && actions.fieldDefaults[selectedActionData.service.module_name]

      const icon = selectedActionData.service ? (
        <img
          className="applet-composer-sidebar-service-logo"
          src={selectedActionData.service.lrg_monochrome_image_url}
          title={selectedActionData.service.name}
          alt={selectedActionData.service.name}
        />
      ) : (
        <ActionIcon title="Action" />
      )

      const iconBackgroundColor = selectedActionData.service && `#${selectedActionData.service.brand_color}`

      let filteredActionServices = services.actionServices
      let filteredSelectedServiceActions = selectedServiceActionsArray

      const currentTrigger = this.currentTrigger()
      if (currentTrigger) {
        filteredActionServices = services.actionServices
          .filter(as => !as.incompatible_channels.includes(currentTrigger.channel_module_name))
          .filter(s => {
            return (
              s.public_actions.filter(a => !a.incompatible_triggers.includes(currentTrigger.full_module_name)).length >
              0
            )
          })

        if (selectedServiceActionsArray) {
          filteredSelectedServiceActions = selectedServiceActionsArray.filter(
            a => !a.incompatible_triggers.includes(currentTrigger.full_module_name)
          )
        }
      }

      return (
        <SectionWithIcon
          key={index}
          icon={icon}
          iconBackgroundColor={iconBackgroundColor}
          label="Then"
          last={isAppletPublished && index === applet.applet_actions.length - 1}
          sectionId={`action-${index}`}
        >
          <div className="fieldset composer-fieldset">
            <TandaComposer
              contextChannelId={contextChannelId}
              services={filteredActionServices}
              onServiceSelect={service => {
                reduxActions.selectActionService(index, service, currentTrigger)
              }}
              selectedService={selectedActionData.service}
              selectedServiceTandas={
                // Don't show the options unless we either have loaded the field
                // defaults for the current trigger or have already chosen an
                // action
                (fieldDefaults || selectedActionData.action) && filteredSelectedServiceActions
              }
              onTandaSelect={action => {
                reduxActions.selectAction(index, action, fieldDefaults[action.id])
              }}
              selectedTanda={selectedActionData.action}
              onRemoveTanda={() => reduxActions.removeAction(index)}
              tandaType="action"
              note="Every Applet ends with at least one action from one of the services on IFTTT."
              enabled={!applet.published}
            >
              {this.actionFieldsComponent(appletAction, index, currentTrigger)}
            </TandaComposer>
          </div>
        </SectionWithIcon>
      )
    })
  }

  actionFieldsComponent(appletAction, index, currentTrigger) {
    const { actions, applet, errors, reduxActions, services, isMaker } = this.props
    const { channel_module_name, module_name, fields } = appletAction

    if (!fields.length) {
      return null
    }

    const selectedService = channel_module_name && services.byModuleName[channel_module_name]

    const selectedAction = selectedService && (actions.byService[channel_module_name] || {})[module_name]

    const fieldsByName = selectedAction && keyBy(selectedAction.fields, af => af.name)

    const ingredientsMetadata = formatIngredientsMetadata(currentTrigger, this.currentQueries(), services.byModuleName)

    return (
      <TandaFields
        tandaType="action"
        appletFields={fields}
        fieldsByName={fieldsByName}
        onChange={(fieldIndex, field, data) => reduxActions.updateActionField(index, fieldIndex, field, data)}
        onInvalidResolution={reduxActions.invalidFieldResolution}
        ingredientsMetadata={ingredientsMetadata}
        platformComposer={true}
        errors={errors.forApplet.select(`/applet/applet_actions/${index}/fields`)}
        invalidRemoteFields={errors.invalidRemoteFields}
        canEditHiddenField={!applet.published}
        canEditHiddenValueField={true}
        minimalView={isMaker && !applet.id}
      />
    )
  }

  triggerComposer() {
    const { applet, contextChannelId, reduxActions, services, triggers } = this.props
    const { channel_module_name, module_name } = applet.applet_trigger

    const selectedService = channel_module_name && services.byModuleName[channel_module_name]

    const selectedServiceTriggers = selectedService && triggers.byService[channel_module_name]

    const selectedServiceTriggersArray = selectedServiceTriggers && Object.values(selectedServiceTriggers)

    const selectedTrigger = module_name && selectedServiceTriggers && selectedServiceTriggers[module_name]

    const icon = selectedService ? (
      <img
        className="applet-composer-sidebar-service-logo"
        src={selectedService.lrg_monochrome_image_url}
        title={selectedService.name}
        alt={selectedService.name}
      />
    ) : (
      <TriggerIcon title="Trigger" />
    )

    const iconBackgroundColor = selectedService && `#${selectedService.brand_color}`

    let onTriggerSelect = reduxActions.selectTrigger
    const previoslySelectedActions = applet.applet_actions.filter(aa => aa.module_name)
    if (previoslySelectedActions.length) {
      onTriggerSelect = event => {
        reduxActions.selectTrigger(event, previoslySelectedActions)
      }
    }

    return (
      <SectionWithIcon icon={icon} iconBackgroundColor={iconBackgroundColor} label="If" sectionId={"trigger"}>
        <div className="fieldset composer-fieldset">
          <TandaComposer
            contextChannelId={contextChannelId}
            services={services.triggerServices}
            onServiceSelect={reduxActions.selectTriggerService}
            selectedService={selectedService}
            selectedServiceTandas={selectedServiceTriggersArray}
            onTandaSelect={onTriggerSelect}
            selectedTanda={selectedTrigger}
            tandaType="trigger"
            note="Every Applet starts with a trigger from one of the services on IFTTT."
            onRemoveTanda={reduxActions.removeTrigger}
            enabled={!applet.published}
          >
            {this.triggerFieldsComponent()}
          </TandaComposer>
        </div>
      </SectionWithIcon>
    )
  }

  triggerFieldsComponent() {
    const { applet, errors, reduxActions, services, triggers, isMaker } = this.props
    const { channel_module_name, module_name, fields } = applet.applet_trigger

    if (!fields.length) {
      return null
    }

    const selectedService = channel_module_name && services.byModuleName[channel_module_name]

    const selectedTrigger = selectedService && (triggers.byService[channel_module_name] || {})[module_name]

    const fieldsByName = selectedTrigger && keyBy(selectedTrigger.fields, tf => tf.name)

    return (
      <TandaFields
        tandaType="trigger"
        appletFields={fields}
        fieldsByName={fieldsByName}
        onChange={reduxActions.updateTriggerField}
        onInvalidResolution={reduxActions.invalidFieldResolution}
        ingredientsMetadata={{}}
        errors={errors.forApplet.select(`/applet/applet_trigger/fields`)}
        invalidRemoteFields={errors.invalidRemoteFields}
        canEditHiddenField={!applet.published}
        canEditHiddenValueField={!applet.published}
        minimalView={isMaker && !applet.id}
      />
    )
  }

  addQueryElement() {
    const { applet, reduxActions, canQuery } = this.props
    const { published, applet_trigger } = applet

    const buttonDisabled = !applet_trigger.module_name || published
    const fieldsetClass = classNames("fieldset", "composer-fieldset", {
      "composer-fieldset-disabled": buttonDisabled,
    })

    if (canQuery) {
      return (
        <div className={fieldsetClass}>
          <div className="composer-add-field field">
            <input
              disabled={buttonDisabled}
              onClick={reduxActions.addQuery}
              type="button"
              className="button-tertiary"
              value="Add query"
            />
            {buttonDisabled && (
              <div className="composer-add-field-message">
                <b>Add a trigger to unlock adding a query.</b>
              </div>
            )}
          </div>
        </div>
      )
    } else {
      return (
        <p className="composer-add-field-message">
          <b>Queries are a Pro+ feature.</b>{" "}
          <a href={this.props.proPlusSubscriptionUrl} target="_blank" rel="noopener noreferrer" className="link-inline">
            Upgrade
          </a>{" "}
          to use queries and more in your Applets.
        </p>
      )
    }
  }

  addActionElement() {
    const { applet, reduxActions, canMultiAction } = this.props
    const { published, applet_trigger } = applet

    if (applet.applet_actions.length > 0 && !canMultiAction) {
      return (
        <p className="composer-add-field-message">
          <b>Multi-actions are a Pro feature.</b>{" "}
          <a
            href={this.props.proSubscriptionUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Upgrade
          </a>{" "}
          to use multi-actions and more in your Applets.
        </p>
      )
    }

    const buttonDisabled = !applet_trigger.module_name || published
    const fieldsetClass = classNames("fieldset", "composer-fieldset", {
      "composer-fieldset-disabled": buttonDisabled,
    })

    return (
      <div className={fieldsetClass}>
        <div className="composer-add-field field">
          <input
            disabled={buttonDisabled}
            onClick={reduxActions.addAction}
            type="button"
            className="button-tertiary"
            value="Add action"
          />
          {buttonDisabled && (
            <div className="composer-add-field-message">
              <b>Add a trigger to unlock adding an action.</b> Every Applet ends with at least one action.
            </div>
          )}
        </div>
      </div>
    )
  }

  genericErrors(errors) {
    const handledPrefixes = [
      "/applet/name",
      "/applet/description",
      "/applet/filter_code",
      "/applet/applet_trigger",
      "/applet/applet_actions/",
      "/applet/applet_queries/",
    ]

    // Any errors that aren't handled by children should be displayed in a list
    // at the top of the form. It would be nice if this were DRYer/cleaner but
    // it's probably not worth it.
    return flatMap(errors.all, (value, key) => {
      if (handledPrefixes.some(pref => key.indexOf(pref) === 0)) {
        return []
      } else {
        return value
      }
    })
  }

  render() {
    const {
      actions,
      queries,
      applet,
      appletFilterTransformTemplate,
      errors,
      isMaker,
      reduxActions,
      services,
      triggers,
      canFilter,
      canDelay,
      canTag,
      docsUrl,
      tagOptions,
    } = this.props

    if (errors.missingPreviewAccess) {
      return <p>Sorry, this Applet depends on a preview service that you don’t currently have access to.</p>
    }

    if (applet.isLoading) {
      return <Spinner />
    }

    const composerClassName = classNames("form", "applet-composer", {
      "applet-composer-with-right-gutter": applet.filter_code != null,
    })

    const genericErrors = this.genericErrors(errors.forApplet)

    return (
      <div className={composerClassName}>
        <header className="composer-header">
          <a href={docsUrl} target="_blank" rel="noopener noreferrer" className="link-standalone">
            Need help {applet.id ? "editing this" : "creating an"} Applet?{" "}
          </a>
        </header>

        <div className="form-section">
          <VisibilityInfo applet={applet} />

          <div className="composer-generic-errors">
            <InlineErrors errors={genericErrors} />
          </div>

          {this.triggerComposer()}

          {this.queryComposers()}

          {!applet.published && (
            <SectionWithIcon icon={<ActionIcon title="Query" />} label="With">
              {this.addQueryElement()}
            </SectionWithIcon>
          )}

          <SectionWithIcon
            icon={<FilterIcon title="Filter" />}
            label="When"
            iconBackgroundColor={applet.filter_code != null && canFilter ? colors.primaryTextColor : null}
          >
            <FilterCodeComposer
              enabled={!!(applet.applet_trigger.module_name && applet.applet_actions.some(aa => aa.module_name))}
              canFilter={canFilter}
              docsUrl={docsUrl}
              proPlusSubscriptionUrl={this.props.proPlusSubscriptionUrl}
              contextTemplate={appletFilterTransformTemplate}
              filterCode={applet.filter_code}
              actions={actions}
              applet={applet}
              queries={queries}
              services={services}
              triggers={triggers}
              onChange={filter_code => reduxActions.updateAppletMetadata({ filter_code })}
              errors={errors.forApplet.getAll(`/applet/filter_code`)}
            />
          </SectionWithIcon>

          <SectionWithIcon icon={<DelayIcon title="Delay" />} label="Wait">
            <Delay
              enabled={!!applet.applet_trigger.module_name}
              canDelay={canDelay}
              onChange={actionsDelay => reduxActions.updateAppletMetadata({ default_delay: actionsDelay })}
              proPlusSubscriptionUrl={this.props.proPlusSubscriptionUrl}
            />
          </SectionWithIcon>

          {this.actionComposers(applet.published)}

          {!applet.published && (
            <SectionWithIcon icon={<ActionIcon title="Action" />} label="Then" last>
              {this.addActionElement()}
            </SectionWithIcon>
          )}

          <AppletMetadata
            applet={applet}
            services={services}
            update={reduxActions.updateAppletMetadata}
            errors={errors.forApplet.select("/applet")}
            showPrimaryService={isMaker}
            canTag={canTag}
            tagOptions={tagOptions}
          />
        </div>

        <div className="actions">
          <div className="action-right">
            <a
              href={`${this.props.appletsUrl}${applet.id ? `/${applet.id}` : ""}`}
              className="button-secondary button-cancel-link"
              data-open-in-current-window={true}
            >
              Cancel
            </a>

            <button
              onClick={this.createApplet}
              name="button"
              type="submit"
              className="button-secondary"
              disabled={!isAppletComplete(applet) || applet.isSending}
            >
              {applet.published ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    )
  }
}

AppletComposerUI.propTypes = {
  appletFilterTransformTemplate: PropTypes.string.isRequired,
  isMaker: PropTypes.bool.isRequired,
  canFilter: PropTypes.bool.isRequired,
  canDelay: PropTypes.bool.isRequired,
  canQuery: PropTypes.bool.isRequired,
  canTag: PropTypes.bool.isRequired,
  canMultiAction: PropTypes.bool.isRequired,
  contextChannelId: PropTypes.string.isRequired,
  isSuggested: PropTypes.bool.isRequired,
  ...mapValues(reducers, _ => PropTypes.object.isRequired),
  appletsUrl: PropTypes.string.isRequired,
  appletShowUrl: PropTypes.string.isRequired,
  docsUrl: PropTypes.string.isRequired,
  proPlusSubscriptionUrl: PropTypes.string.isRequired,
  proSubscriptionUrl: PropTypes.string.isRequired,
  createOrUpdateAppletUrl: PropTypes.string.isRequired,
}

function VisibilityInfo({ applet }) {
  if (!applet.id) {
    return null
  }

  let message = "This Applet is private and can only be accessed by you."
  if (applet.archived) {
    message =
      "This Applet is archived. You can’t change its triggers, queries, or actions. It’s only visible to users who have already enabled it."
  } else if (applet.published) {
    message = "This Applet is published. You can’t change its triggers, queries, or actions."
  }

  const icon = applet.published ? <PublicIcon /> : <PrivateIcon />

  return <MessageWithIcon icon={icon} text={message} />
}

VisibilityInfo.propTypes = {
  applet: PropTypes.object,
}

function mapDispatchToProps(dispatch) {
  // Use `reduxActions` to avoid conflicting with the `actions` reducer
  return { reduxActions: bindActionCreators(actions, dispatch) }
}

// Map Redux state directly to props, since this is the only smart component.
export default connect(state => state, mapDispatchToProps)(AppletComposerUI)
